import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown, MessageSquare, Eye, KeyRound, Sparkles,
  ShieldCheck, Lock, Clock, Headphones, BookOpen, PlayCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import BookPreviewModal from "../components/BookPreviewModal";
import EgVideoModal from "../components/EgVideoModal";
import EgHeaderNav from "../components/EgHeaderNav";
import TestimonialCard from "../components/TestimonialCard";
import ExitIntentPopup from "../components/ExitIntentPopup";
import Logo from "../components/Logo";
import { trackVisit, createOrder, trackCtaClick } from "../lib/api";
import { trackViewContent, trackInitiateCheckout, trackAddToCart, trackClickButton } from "../lib/tiktok";
import { startSessionTracker } from "../lib/sessionTracker";
import { egPrice, EG_MAIN_EGP, EG_EXIT_EGP } from "../lib/formatEg";
import { toast } from "sonner";

const DARK_TEXTURE = "https://static.prod-images.emergentagent.com/jobs/d18eb75d-910a-420b-9f07-c4ad3b278ed1/images/af9fc0f88b4a0e9b163fbc29632f76119c5309fa5d027dffd33d7e2433633aa9.png";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/d18eb75d-910a-420b-9f07-c4ad3b278ed1/images/04bb569ebc03b0189a797318d05798b624ee2b1027e038468863044298e538b4.png";
// Egypt-only mid-page image (replaces the generic mockup used on the global page).
const EG_MID_IMAGE = "/assets/eg-silence.png";

const features = [
  { icon: MessageSquare, title: "فتح الكلام بدون توتر", text: "إزاي تبدأ من الموقف نفسه بدل الأسئلة المحفوظة والجمل المصطنعة." },
  { icon: Eye, title: "التعامل مع الصمت", text: "إزاي تتصرف لما الكلام يقف فجأة، من غير ما تبان مرتبك أو مستعجل تنقذ الموقف." },
  { icon: Crown, title: "الكلام مع بنت عاجباك", text: "إزاي تتكلم بثبات من غير مبالغة، من غير ضغط، ومن غير ما تحس إن كل كلمة امتحان." },
  { icon: KeyRound, title: "قراءة إشارات التواصل", text: "إزاي تلاحظ الاهتمام، التردد، الملل، أو المجاملة من تفاصيل صغيرة في الكلام والطاقة." },
  { icon: Sparkles, title: "الانطباع الأول", text: "إزاي تبني إحساس أقوى من أول دقيقة، قبل ما تحتاج تشرح نفسك أو تثبت قيمتك." },
];

const faqs = [
  { q: "هل الكتاب عن التعارف فقط؟", a: "لا. الكتاب عن فتح الكلام، الحضور، والتعامل مع المواقف الاجتماعية. لكنه يتناول الكلام مع البنات والتعارف لأن دي من أكثر المواقف اللي بيظهر فيها التوتر." },
  { q: "هل فيه جمل جاهزة؟", a: "فيه أمثلة ومداخل للحوار، لكن الهدف مش الحفظ. الهدف إنك تفهم طريقة الدخول في الكلام بثبات." },
  { q: "هل مناسب لو أنا مش اجتماعي؟", a: "نعم. الكتاب مناسب جداً لو بتفكر كتير قبل ما تتكلم، أو بتحس إن حضورك بيقل لما تبقى قدام شخص يهمك انطباعه." },
  { q: "هل النسخة صوتية؟", a: "نعم. تحصل على الكتاب الرقمي ومعه نسخة صوتية مجاناً." },
  { q: "هل الدفع آمن؟", a: "نعم. الدفع يتم عبر بوابة دفع آمنة، ولا يتم تخزين بيانات الدفع على الموقع." },
  { q: "هل يوجد ضمان؟", a: "نعم. إذا شعرت أن المحتوى غير مناسب لك، يمكنك طلب استرداد خلال 7 أيام." },
];

const main = egPrice(EG_MAIN_EGP, { withAnchor: true }); // 499 جنيه (anchor 699)
const offer = egPrice(EG_EXIT_EGP, { withAnchor: true }); // 399 جنيه (exit-intent, anchor 699)

export default function LandingEg() {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    trackVisit("landing-eg");
    trackViewContent("pdf");
    // Mark this session as Egypt funnel so Snap pixel is suppressed
    // even on /checkout/:id and /success/:id (they don't have /eg in path).
    try { sessionStorage.setItem("bb_funnel", "eg"); } catch (_) { /* ignore */ }
    const stop = startSessionTracker("landing-eg");
    return stop;
  }, []);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Egypt CTA: create order → navigate to /checkout/:orderId, where the
  // Geidea drop-in widget is rendered inline next to the order summary.
  const openCheckout = (source, pkg = "pdf_audio_eg") => {
    trackClickButton(source);
    trackCtaClick(`${source}-eg`);
    trackAddToCart(pkg);
    startCheckout(pkg);
  };

  const startCheckout = async (pkg) => {
    if (loading) return;
    setLoading(true);
    trackInitiateCheckout(pkg);
    try {
      const r = await createOrder(pkg);
      navigate(`/checkout/${r.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بدء عملية الدفع، حاول مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040914] text-white overflow-x-hidden pb-24 md:pb-0" data-testid="landing-eg-root">
      <ExitIntentPopup
        priceOverride={{ main, offer, localUnit: "جنيه" }}
        onClaim={() => openCheckout("exit-intent", "pdf_audio_eg_exit")}
      />

      <EgHeaderNav variant="absolute" />

      {/* HERO */}
      <section className="relative pt-24 pb-12 sm:pt-40 sm:pb-24 grain-bg gold-glow">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url('${DARK_TEXTURE}')` }}></div>
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 text-center">
          <div className="flex justify-center mb-6 sm:mb-8 fade-up">
            <Logo className="h-24 w-24 sm:h-36 sm:w-36" />
          </div>
          <div className="inline-block text-[#C5A059] text-[10px] sm:text-sm tracking-[0.35em] sm:tracking-[0.4em] mb-4 sm:mb-5 fade-up delay-100 px-2 leading-relaxed">
            كتاب عملي للحضور، الثقة، وفتح الكلام بدون توتر
          </div>
          <h1 className="font-display text-[24px] leading-[1.35] sm:text-4xl sm:leading-[1.3] lg:text-5xl font-black tracking-tight text-[#C5A059] fade-up delay-200">
            الكتاب الأزرق بيتكلم عن لحظة بسيطة،<br className="hidden sm:block"/> لكن رجّالة كتير يعرفوها كويس:
          </h1>
          <p className="mt-5 sm:mt-7 text-sm sm:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed fade-up delay-300">
            لحظة تقعد قدام بنت عاجباك، أو تدخل أول تعارف، أو تكون في قعدة عايز تسيب فيها انطباع كويس… <span className="text-[#C5A059]">وفجأة تحس إن الكلام بقى أصعب من المعتاد.</span>
          </p>

          <div className="mt-7 sm:mt-9 flex flex-col items-center gap-3.5 sm:gap-4">
            {/* Single hero CTA — opens the EgVideoModal ("first secret" teaser).
                The buy CTA + price lives INSIDE the video modal, under the player. */}
            <div className="w-full max-w-md mx-auto fade-up delay-400" data-testid="hero-cta-row">
              <Button
                data-testid="hero-video-cta"
                onClick={() => { trackClickButton("hero-video-open"); trackCtaClick("hero-video-open-eg"); setVideoOpen(true); }}
                className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse h-auto px-6 py-5 sm:py-6"
              >
                <span className="flex items-center justify-center gap-2.5 leading-tight" dir="rtl">
                  <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" strokeWidth={2} />
                  <span className="flex flex-col items-center">
                    <span className="text-[14px] sm:text-lg">اسمع أول سر من الكتاب</span>
                    <span className="text-[10px] sm:text-xs font-semibold text-[#040914]/75">شغّل الفيديو دلوقتي مجاناً</span>
                  </span>
                </span>
              </Button>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-xs sm:text-sm text-[#C5A059]/90 fade-up delay-500">
              <Headphones className="w-4 h-4" />
              <span>كتاب رقمي + نسخة صوتية · استلام فوري · دفع آمن · ضمان 7 أيام</span>
            </div>
            <div className="flex flex-col items-center gap-2 fade-up delay-700">
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[11px] sm:text-xs text-[#A1B0C6]">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]"/> ضمان الخصوصية</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#C5A059]"/> تسليم فوري</span>
                <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-[#C5A059]"/> دفع آمن</span>
              </div>
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-[#C5A059]" data-testid="refund-guarantee-hero">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>ضمان استرداد خلال 7 أيام إذا لم تجد المحتوى مناسباً لك.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — Pain */}
      <section className="relative py-14 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
              المشكلة مش إنك مش عارف تتكلم…<br className="sm:hidden"/> المشكلة إنك <span className="text-[#C5A059]">بتتوتر لما الموضوع يهمك</span>
            </h2>
            <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-[#A1B0C6] max-w-3xl mx-auto leading-relaxed">
              ممكن تبقى طبيعي جداً مع صحابك. بتهزر، ترد، وتتكلم من غير تفكير.
              <br/>لكن أول ما تبقى قدام بنت عاجباك، أو في أول تعارف، أو في قعدة فيها ناس عايز تسيب عندهم انطباع، فجأة كل حاجة بتتغير.
            </p>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-white/90">تبدأ تفكر:</p>
          </div>
          <div className="grid gap-3 sm:gap-5 sm:grid-cols-2 max-w-4xl mx-auto">
            {[
              "أبدأ الكلام إزاي؟",
              "أسأل ولا أعلق؟",
              "أقول حاجة خفيفة ولا هتبان سخيفة؟",
              "أسكت ولا كده هبان ممل؟",
              "هي مهتمة فعلاً ولا بتجامل؟",
              "الكلام برد ولا لسه فيه فرصة؟",
              "هو باين عليا إني متوتر؟",
            ].map((p, i) => (
              <div
                key={i}
                data-testid={`pain-point-${i}`}
                className="bg-[#0B1D3A]/60 backdrop-blur-sm border-r-2 border-[#C5A059] p-4 sm:p-6 text-sm sm:text-base text-white/90 leading-relaxed"
              >
                {p}
              </div>
            ))}
          </div>
          <div className="mt-10 sm:mt-14 max-w-3xl mx-auto" dir="rtl" data-testid="full-version-block">
            <p className="text-sm sm:text-lg text-white/90 leading-relaxed text-center">
              النسخة الكاملة من <span className="text-[#C5A059] font-bold">الكتاب الأزرق</span> تدخل بشكل أعمق في المواقف اللي بتتكرر عند رجالة كتير:
            </p>
            <ul className="mt-6 sm:mt-8 divide-y divide-[#C5A059]/15 border-y border-[#C5A059]/15">
              {[
                "إزاي تبدأ الكلام مع بنت عاجباك من غير ما تبان متوتر أو مندفع",
                "إزاي تفتح الحوار من الموقف نفسه بدل الجمل المحفوظة",
                "إزاي تتعامل مع الصمت لما الكلام يقف فجأة",
                "إزاي تفرق بين الاهتمام، المجاملة، والتردد",
                "إزاي تقلل التفكير الزائد قبل أول جملة",
                "إزاي تتكلم أقل لكن بتأثير أوضح",
                "إزاي تبني انطباع أول أقوى من غير ما تحاول تثبت نفسك",
                "إزاي تستخدم هدوءك كقوة بدل ما يكون سبب إنك تختفي",
              ].map((line, i) => (
                <li
                  key={i}
                  data-testid={`full-version-item-${i}`}
                  className="py-3 sm:py-4 text-sm sm:text-base text-white/90 leading-relaxed text-center"
                >
                  {line}
                </li>
              ))}
            </ul>

            <p className="mt-9 sm:mt-12 text-base sm:text-2xl font-black text-[#C5A059] text-center">
              الكتاب مش عن التلاعب.
            </p>
            <p className="mt-4 text-sm sm:text-base text-white/90 leading-relaxed text-center">
              ومش عن إنك تخلي أي بنت تعجب بيك غصب عنها.
              <br/>
              ومش عن إنك تمثل دور مش دورك.
            </p>
            <p className="mt-5 sm:mt-6 text-sm sm:text-base text-white/90 leading-relaxed text-center">
              هو عن إنك تبقى <span className="text-[#C5A059] font-bold">أهدأ</span> في اللحظة اللي كنت بتتوتر فيها.
              <br/>
              <span className="text-[#C5A059] font-bold">أوضح</span> في اللحظة اللي كنت بتتلخبط فيها.
              <br/>
              <span className="text-[#C5A059] font-bold">وأكثر ثباتاً</span> في المواقف اللي الانطباع الأول فيها بيفرق.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 — Women / Dating */}
      <section className="relative py-14 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
            التعارف مش محتاج تمثيل…<br className="sm:hidden"/> <span className="text-[#C5A059]">محتاج ثبات</span>
          </h2>
          <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-[#A1B0C6] max-w-3xl mx-auto leading-relaxed">
            فيه رجالة أول ما يعجبوا بواحدة، بيقعوا في واحد من 3 فخاخ:
          </p>
          <div className="mt-7 sm:mt-10 grid gap-2.5 sm:gap-4 sm:grid-cols-1 max-w-2xl mx-auto text-right" dir="rtl">
            {[
              "إما يتكلموا كتير زيادة عشان يغطوا التوتر.",
              "أو يسكتوا تماماً عشان خايفين يقولوا حاجة غلط.",
              "أو يحاولوا يبانوا جامدين، فالكلام يطلع مصطنع.",
            ].map((p, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/85">
                · {p}
              </div>
            ))}
          </div>
          <p className="mt-7 sm:mt-10 text-sm sm:text-lg text-[#C5A059] font-bold leading-relaxed">
            والثلاثة بيبانوا.
          </p>
          <p className="mt-4 text-sm sm:text-base text-[#A1B0C6] max-w-3xl mx-auto leading-relaxed">
            البنت مش محتاجة تسمع منك كلام مبالغ فيه. ولا محتاجة تحس إنك بتعمل performance. ولا محتاجة تشوفك بتثبت نفسك في كل جملة.
          </p>
          <p className="mt-4 text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed">
            اللي بيشد الانتباه فعلاً هو إنك تكون <span className="text-[#C5A059] font-bold">ثابت</span>.
            <br/>عارف تدخل الكلام. عارف تسمع. عارف ترد. وعارف تتعامل مع الصمت من غير ما تتوتر.
          </p>
          <p className="mt-6 sm:mt-8 font-display text-base sm:text-2xl lg:text-3xl font-black text-[#C5A059] max-w-3xl mx-auto leading-snug">
            الجاذبية مش بس في الكلام اللي بتقوله…
            <br className="sm:hidden"/>
            الجاذبية في حالتك وأنت بتقوله.
          </p>
        </div>
      </section>

      {/* Section 4 — Product reveal with the user-provided silence image */}
      <section className="relative py-14 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          <div className="flex justify-center order-1 lg:order-1">
            <img
              src={EG_MID_IMAGE}
              alt="الصمت مش نهاية الحوار"
              className="w-full max-w-xs sm:max-w-md rounded-lg shadow-[0_30px_60px_rgba(0,0,0,0.7),0_0_30px_rgba(197,160,89,0.18)]"
              draggable={false}
              data-testid="eg-mid-image"
            />
          </div>
          <div className="order-2 lg:order-2 text-right" dir="rtl">
            <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
              ما هو <span className="text-[#C5A059]">الكتاب الأزرق</span>؟
            </h2>
            <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-[#A1B0C6] leading-relaxed">
              الكتاب الأزرق هو كتاب رقمي عملي عن فتح الكلام، الثقة، والتعامل مع التوتر في المواقف الاجتماعية، خصوصاً مواقف التعارف والانطباع الأول.
            </p>
            <p className="mt-3 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
              مش كتاب تنمية بشرية عام. ومش "خليك واثق في نفسك" وخلاص. ومش شوية جمل محفوظة تقولها لأي بنت.
            </p>
            <p className="mt-3 text-sm sm:text-base text-white/90 leading-relaxed">
              هو كتاب يساعدك تفهم:
            </p>
            <ul className="mt-3 sm:mt-4 space-y-2 text-sm sm:text-base text-white/85">
              {[
                "تبدأ الكلام إزاي من غير توتر",
                "تتعامل مع الصمت إزاي",
                "تعرف الفرق بين الاهتمام والمجاملة",
                "تفتح كلام طبيعي من غير ما تبان بتحاول",
                "تسيب انطباع أول أقوى",
                "تتكلم بثبات لما تكون قدام شخص يهمك رأيه",
                "تقلل التفكير الزائد اللي بيبوظ اللحظة",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Section 5 — Core Promise */}
      <section className="relative py-14 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
            تعلّم تدخل الحوار من غير ما تبان <span className="text-[#C5A059]">متوتر أو محتاج تثبت نفسك</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-lg text-[#A1B0C6] leading-relaxed">
            أغلب الرجالة مش محتاجة شخصية جديدة. محتاجة تفهم اللعبة الاجتماعية بشكل أوضح.
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/90 leading-relaxed">
            لأن المشكلة غالباً مش إنك مش interesting. المشكلة إنك بتدخل الحوار بطريقة <span className="text-[#C5A059] font-bold">تكشف التوتر</span>.
          </p>
          <div className="mt-6 grid gap-2.5 sm:gap-3 sm:grid-cols-1 max-w-xl mx-auto text-right" dir="rtl">
            {[
              "مرة بسؤال مباشر زيادة.",
              "مرة بسكوت طويل.",
              "مرة بهزار في غير وقته.",
              "مرة بمحاولة إنك تبان جامد.",
              "ومرة بتفكير زائد يخليك متأخر في الرد.",
            ].map((t, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/85">
                · {t}
              </div>
            ))}
          </div>
          <p className="mt-7 sm:mt-8 text-sm sm:text-lg text-white/90 leading-relaxed">
            الكتاب الأزرق يساعدك تشوف التفاصيل دي بوضوح، وتبدأ تتعامل معها <span className="text-[#C5A059] font-bold">بثبات أكبر</span>.
          </p>
        </div>
      </section>

      {/* Section 6 — Five Pillars */}
      <section className="relative py-14 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-9 sm:mb-14">
            <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
              5 مهارات هتغير طريقة دخولك<br className="sm:hidden"/> <span className="text-[#C5A059]">لأي حوار</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`feature-card-${i}`}
                  className="group bg-[#0B1D3A]/70 backdrop-blur-sm border border-[#C5A059]/20 p-5 sm:p-7 rounded-sm hover:border-[#C5A059]/60 transition-colors"
                >
                  <div className="w-11 h-11 sm:w-12 sm:h-12 mb-4 sm:mb-5 rounded-sm bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-[#040914] transition-colors">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-base sm:text-xl font-bold mb-2 leading-snug">{i + 1}. {f.title}</h3>
                  <p className="text-sm sm:text-base text-[#A1B0C6] leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 sm:mt-16 text-center" data-testid="features-preview-block">
            <button
              type="button"
              onClick={() => { trackClickButton("preview-features-eg"); trackCtaClick("preview-eg"); setPreviewOpen(true); }}
              data-testid="features-preview-link"
              className="group relative inline-flex items-center gap-2 sm:gap-3 shimmer-border rounded-full px-5 py-3 sm:px-7 sm:py-4 text-[#C5A059] hover:text-[#E5C07B] transition-colors text-sm sm:text-lg font-bold"
            >
              <span className="absolute -top-2 -right-2 sm:-top-2.5 sm:-right-2.5 bg-[#C5A059] text-[#040914] text-[9px] sm:text-[10px] font-black px-2 py-0.5 rounded-full shadow-md">مجاناً</span>
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
              <span>شاهد 3 صفحات من داخل الكتاب قبل الشراء</span>
              <span className="text-[#C5A059] text-base sm:text-lg leading-none group-hover:translate-x-[-3px] transition-transform">←</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 6 — What you get */}
      <section className="relative py-14 sm:py-28 border-t border-[#C5A059]/10 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url('${HERO_BG}')` }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#040914]/70 via-[#040914]/85 to-[#040914]"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
            ماذا ستحصل عليه <span className="text-[#C5A059]">فوراً بعد الدفع</span>؟
          </h2>
          <ul className="mt-8 sm:mt-10 grid gap-2.5 sm:gap-4 sm:grid-cols-2 text-right max-w-3xl mx-auto" dir="rtl">
            {[
              "الكتاب الأزرق الكامل بصيغة رقمية",
              "نسخة صوتية مجانية للاستماع",
              "أمثلة عملية لفتح الحوار",
              "جمل تساعدك تتعامل مع الصمت",
              "طرق لتقليل التوتر في المواقف الاجتماعية",
              "أفكار للتعامل مع أول تعارف وانطباع أول",
              "استلام فوري بعد الدفع",
              "ضمان استرداد خلال 7 أيام",
            ].map((t, i) => (
              <li key={i} className="flex items-center gap-3 bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/90 text-right">
                <span className="w-5 h-5 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center text-xs font-bold shrink-0">✓</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 8 — Value Anchor */}
      <section className="relative py-14 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
            <span className="text-[#C5A059]">{main.primary}</span> مقابل شيء<br className="sm:hidden"/> هتستخدمه في مواقف كتير
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-lg text-[#A1B0C6] leading-relaxed">
            الكتاب مش قراءة مرة وخلاص.
            <br/>ده محتوى ترجع له قبل موقف مهم:
          </p>
          <div className="mt-6 grid gap-2.5 sm:gap-3 sm:grid-cols-2 max-w-xl mx-auto text-right" dir="rtl">
            {[
              "قبل ديت",
              "قبل أول مقابلة",
              "قبل قعدة فيها ناس جديدة",
              "قبل ما تكلم بنت مهتم بيها",
              "قبل موقف عايز فيه تسيب انطباع أقوى",
              "قبل لحظة بتخاف فيها من الصمت أو الارتباك",
            ].map((t, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/90">
                · {t}
              </div>
            ))}
          </div>
          <p className="mt-7 sm:mt-8 text-sm sm:text-lg text-white/90 leading-relaxed">
            تدفعها <span className="text-[#C5A059] font-bold">مرة واحدة</span>، وتحتفظ بالكتاب والنسخة الصوتية معاك.
          </p>
        </div>
      </section>

      {/* Section 9 — Preview Section */}
      <section className="relative py-12 sm:py-20 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display text-[20px] leading-[1.3] sm:text-3xl lg:text-4xl font-black">
            شاهد 3 صفحات من داخل الكتاب <span className="text-[#C5A059]">قبل الشراء</span>
          </h2>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
            قبل ما تشتري، تقدر تشوف عينة قصيرة من داخل الكتاب.
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/90">العينة هتوريك:</p>
          <div className="mt-4 grid gap-2 sm:gap-2.5 sm:grid-cols-1 max-w-md mx-auto text-right" dir="rtl">
            {[
              "أسلوب الكتاب",
              "طريقة الشرح",
              "نوع الأمثلة",
              "إزاي بيتعامل مع التوتر، الصمت، وفتح الكلام",
            ].map((t, i) => (
              <div key={i} className="text-sm sm:text-base text-white/85">· {t}</div>
            ))}
          </div>
          <Button
            data-testid="preview-cta-section"
            onClick={() => { trackClickButton("preview-section-eg"); trackCtaClick("preview-eg"); setPreviewOpen(true); }}
            className="mt-5 sm:mt-7 bg-transparent border-2 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-[#040914] font-bold text-sm sm:text-base px-5 sm:px-7 py-4 sm:py-5 rounded-sm"
          >
            <BookOpen className="w-4 h-4 ml-2" />
            شاهد 3 صفحات من داخل الكتاب
          </Button>
          <div className="mt-3 text-xs text-[#A1B0C6]">العينة مجانية ولا تحتاج تسجيل</div>
        </div>
      </section>

      {/* Section 9 — Pricing */}
      <section className="relative py-14 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl lg:text-5xl font-black">
            احصل على الكتاب الأزرق<br/>
            <span className="text-[#C5A059]">الآن</span>
          </h2>

          <div className="mt-7 sm:mt-10 bg-[#0B1D3A]/80 border border-[#C5A059]/30 rounded-sm p-6 sm:p-10 shadow-[0_0_60px_rgba(197,160,89,0.12)]">
            <div className="text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] text-[#C5A059] mb-2">
              الكتاب الأزرق + الملحق الصوتي
            </div>
            {/* Launch price + crossed-out anchor */}
            <div className="flex items-baseline justify-center gap-3 sm:gap-5 flex-wrap" data-testid="pricing-price-row">
              {main.anchor && (
                <span
                  className="text-2xl sm:text-4xl text-[#A1B0C6] line-through decoration-[#A1B0C6]/70 decoration-2"
                  data-testid="pricing-anchor"
                >
                  بدل {main.anchor}
                </span>
              )}
              <span className="font-display text-5xl sm:text-7xl font-black text-[#C5A059] leading-none">{main.primaryNumber} <span className="text-2xl sm:text-4xl">جنيه مصري</span></span>
            </div>
            {main.anchorReason && (
              <div
                className="mt-2 inline-block bg-[#C5A059]/15 border border-[#C5A059]/40 text-[#C5A059] text-[11px] sm:text-xs tracking-[0.15em] font-bold px-3 py-1 rounded-full"
                data-testid="pricing-anchor-reason"
              >
                {main.anchorReason}
              </div>
            )}
            <div className="mt-3 text-xs sm:text-sm text-[#A1B0C6]">عرض خاص لفترة محدودة — يشمل النسخة الصوتية مجاناً</div>

            <ul className="mt-6 sm:mt-8 space-y-2.5 sm:space-y-3 text-right max-w-md mx-auto text-white/90" dir="rtl">
              {[
                "الكتاب الرقمي الكامل",
                "النسخة الصوتية مجاناً",
                "استلام فوري",
                "ضمان 7 أيام",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-3 justify-end text-sm sm:text-base">
                  <span>{t}</span>
                  <span className="w-5 h-5 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center text-xs font-bold shrink-0">✓</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 sm:mt-8">
              <TestimonialCard
                size="md"
                quote='خلصته في يوم. أكتر حاجة عجبتني التطبيقات العملية والأمثلة الواقعية، مش تنظير.'
                name="محمد"
                location="مصر"
              />
            </div>

            <Button
              data-testid="pricing-buy-btn"
              onClick={() => openCheckout("pricing")}
              disabled={loading}
              className="mt-7 sm:mt-8 w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold text-base sm:text-lg py-6 sm:py-7 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.35)] cta-pulse"
            >
              احصل على الكتاب الآن — {main.primary}
            </Button>
            <div className="mt-3 text-[11px] sm:text-xs text-[#A1B0C6]">
              الدفع متاح بالكارت، المحافظ الإلكترونية، وApple Pay
            </div>
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-[#C5A059]"
              data-testid="refund-guarantee-pricing"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>ضمان استرداد خلال 7 أيام إذا لم تجد المحتوى مناسباً لك.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 10 — FAQ */}
      <section className="relative py-14 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-9 sm:mb-12">
            <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] mb-2 sm:mb-3">الأسئلة الشائعة</div>
            <h2 className="font-display text-[22px] leading-[1.3] sm:text-4xl font-black">أسئلة قد تخطر ببالك</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2.5 sm:space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-[#C5A059]/20 bg-[#0B1D3A]/60 rounded-sm px-4 sm:px-5 data-[state=open]:border-[#C5A059]/50"
              >
                <AccordionTrigger className="text-right hover:no-underline font-display font-bold text-sm sm:text-lg py-4 sm:py-5 [&>svg]:text-[#C5A059]" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#A1B0C6] text-sm sm:text-base leading-relaxed pb-4 sm:pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <footer className="relative border-t border-[#C5A059]/15 py-10">
        {/* About-the-author plain text block — moved to the bottom of the page
            as a credibility footer note (no border / CTA styling). */}
        <div className="max-w-3xl mx-auto px-5 sm:px-6 mb-10 sm:mb-14 text-center" data-testid="originator-credit">
          <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] font-bold mb-3">
            عن المؤلف
          </div>
          <p className="text-[13px] sm:text-base text-[#A1B0C6] leading-relaxed" dir="rtl">
            الكتاب ده في الأساس للكاتب <span className="text-[#C5A059] font-bold">"جوردان بيترسون"</span>، عالم نفس وأستاذ جامعي عالمي، صاحب برامج وكتب أثّرت في ملايين الرجال حول العالم، وكتبه باعت أكتر من <span className="text-[#C5A059] font-bold">10 مليون نسخة</span>. إحنا قرينا أعماله ودرسناها بعناية، فهمنا كل أبعادها، وطوعناها عشان تتماشى مع عاداتنا وتقاليدنا.
          </p>
        </div>
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-5">
          <Logo className="h-12 w-12" />
          <div className="text-xs sm:text-sm text-[#A1B0C6] text-center">
            © {new Date().getFullYear()} الكتاب الأزرق — جميع الحقوق محفوظة
          </div>
          <div className="text-xs text-[#A1B0C6] flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#C5A059]"/>
            دفع آمن ومشفّر عبر Geidea
          </div>
        </div>
      </footer>

      <BookPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        pages={["/preview-eg/P1.png", "/preview-eg/P2.png", "/preview-eg/P3.png"]}
        onCta={() => {
          trackClickButton("preview-modal-cta-eg");
          trackCtaClick("preview-modal-cta-eg");
          setPreviewOpen(false);
          openCheckout("preview-modal");
        }}
      />

      {/* Hero "first secret" video modal — locked controls; buy CTA inside. */}
      <EgVideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        onBuy={() => { setVideoOpen(false); openCheckout("video-modal"); }}
        ctaLabel="video-modal-buy-eg"
      />

      <div
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 pt-2 bg-gradient-to-t from-[#040914] via-[#040914]/95 to-transparent transition-all duration-300 ${
          showSticky ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-full opacity-0 pointer-events-none"
        }`}
        data-testid="sticky-cta-wrap"
      >
        <Button
          data-testid="sticky-buy-btn"
          onClick={() => openCheckout("sticky")}
          disabled={loading}
          className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold text-base py-6 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.45)] cta-pulse"
        >
          احصل على الكتاب الآن &nbsp;·&nbsp; {main.primary}
        </Button>
      </div>
    </div>
  );
}
